import { isAxiosError } from "axios";

import { GenericObject } from "src/api/flowTypes";
import { toastActions } from "src/design-system/Toast/utils";
import { errorMessage } from "src/utils/stringUtils";

export const toastError = (
  err: unknown,
  options?: {
    title?: string;
    id?: string;
    dismissable?: boolean;
  },
) => {
  // Toast errors
  // Errors found here from toast
  toastActions.failure({
    title: options?.title || "Error",
    description: errorMessage(err),
    id: options?.id,
    dismissible: options?.dismissable,
  });
};

type WrappableFunction<TArgs extends unknown[], TReturn> = {
  (...args: TArgs): Promise<TReturn>;
};

type WrappableFunctionWithProperties<
  TArgs extends unknown[],
  TReturn,
  TProperties = GenericObject,
> = WrappableFunction<TArgs, TReturn> & TProperties;

/**
 * Higher order function that calls toastError if the
 * supplied function raises an error during execution.
 */
export const wrapWithErrorHandler = <TArgs extends unknown[], TReturn>(
  fn: WrappableFunctionWithProperties<TArgs, TReturn>,
): WrappableFunctionWithProperties<TArgs, TReturn> => {
  const wrappedFn: WrappableFunction<TArgs, TReturn> = async (...args) => {
    try {
      return await fn(...args);
    } catch (err: unknown) {
      toastError(err);
      throw err;
    }
  };

  // Copy additional properties from fn to the wrapped function
  for (const prop in fn) {
    if (Object.hasOwn(fn, prop)) {
      (wrappedFn as unknown as Record<string, unknown>)[prop] = (
        fn as unknown as Record<string, unknown>
      )[prop];
    }
  }

  return wrappedFn as WrappableFunctionWithProperties<TArgs, TReturn>;
};

/**
 * Higher order function that calls toastError if the
 * supplied function raises an error during execution
 * while giving details in case the error is an AxiosError.
 */
export const wrapWithAxiosResponseErrorHandler = <
  TArgs extends unknown[],
  TReturn,
>(
  fn: WrappableFunction<TArgs, TReturn>,
): WrappableFunction<TArgs, TReturn> => {
  const wrappedFn: typeof fn = async (...args) => {
    try {
      return await fn(...args);
    } catch (err: unknown) {
      if (isAxiosError(err) && err.response?.status !== undefined) {
        toastError(errorMessage(err), {
          title: `Error ${err.response.status}`,
        });
        throw Error(err.message);
      }
      toastError(errorMessage(err));
      throw err;
    }
  };

  // Copy additional properties from fn to the wrapped function
  for (const prop in fn) {
    if (Object.hasOwn(fn, prop)) {
      (wrappedFn as unknown as Record<string, unknown>)[prop] = (
        fn as unknown as Record<string, unknown>
      )[prop];
    }
  }

  return wrappedFn;
};
